<template>
    <Combobox v-model="selected" nullable as="div" class="relative">
        <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <SearchIcon class="h-4 w-4 text-slate-400" aria-hidden="true" />
            </div>
            <ComboboxInput
                class="ts-modal-input border-slate-200 !pl-10 !pr-10"
                :class="{ '!border-red-400 !ring-4 !ring-red-200': error }"
                :display-value="(brand) => brand?.name || ''"
                :placeholder="placeholder"
                @change="query = $event.target.value"
                @focus="open = true"
            />
            <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-3">
                <SelectorIcon class="h-5 w-5 text-slate-400" aria-hidden="true" />
            </ComboboxButton>
        </div>

        <TransitionRoot
            leave="transition ease-in duration-100"
            leave-from="opacity-100"
            leave-to="opacity-0"
            @after-leave="query = ''"
        >
            <ComboboxOptions
                class="absolute z-[80] mt-2 max-h-64 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl focus:outline-none"
            >
                <div class="border-b border-slate-100 bg-slate-50 px-3 py-2">
                    <p class="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                        {{ filteredBrands.length }} marca{{ filteredBrands.length === 1 ? '' : 's' }}
                    </p>
                </div>

                <div class="max-h-52 overflow-y-auto py-1">
                    <div
                        v-if="filteredBrands.length === 0"
                        class="px-4 py-6 text-center text-sm text-slate-500"
                    >
                        No se encontraron marcas
                        <span v-if="query"> para “{{ query }}”</span>
                    </div>

                    <ComboboxOption
                        v-for="brand in filteredBrands"
                        :key="brand.id"
                        v-slot="{ active, selected: isSelected }"
                        :value="brand"
                        as="template"
                    >
                        <li
                            class="relative flex cursor-pointer select-none items-center gap-3 px-3 py-2.5 transition"
                            :class="active ? 'bg-orange-50 text-orange-700' : 'text-slate-700'"
                        >
                            <span
                                class="inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white"
                            >
                                <img
                                    v-if="brand.image"
                                    :src="brand.image"
                                    :alt="brand.name"
                                    class="h-6 w-auto object-contain"
                                />
                                <span v-else class="text-xs font-bold text-slate-400">
                                    {{ (brand.name || '?').charAt(0) }}
                                </span>
                            </span>
                            <span class="min-w-0 flex-1 truncate text-sm" :class="isSelected ? 'font-bold' : 'font-medium'">
                                {{ brand.name }}
                            </span>
                            <CheckIcon
                                v-if="isSelected"
                                class="h-4 w-4 shrink-0 text-orange-600"
                                aria-hidden="true"
                            />
                        </li>
                    </ComboboxOption>
                </div>
            </ComboboxOptions>
        </TransitionRoot>
    </Combobox>
</template>

<script>
import {
    Combobox,
    ComboboxInput,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
    TransitionRoot,
} from '@headlessui/vue';
import { CheckIcon, SelectorIcon, SearchIcon } from '@heroicons/vue/solid';

export default {
    components: {
        Combobox,
        ComboboxInput,
        ComboboxButton,
        ComboboxOptions,
        ComboboxOption,
        TransitionRoot,
        CheckIcon,
        SelectorIcon,
        SearchIcon,
    },
    props: {
        modelValue: {
            type: [String, Number],
            default: '',
        },
        options: {
            type: Array,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: 'Buscar y seleccionar marca...',
        },
        error: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            query: '',
            open: false,
            selected: null,
        };
    },
    computed: {
        filteredBrands() {
            const term = this.query.trim().toLowerCase();
            if (!term) return this.options;
            return this.options.filter((brand) =>
                String(brand?.name || '').toLowerCase().includes(term)
            );
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(value) {
                if (value === '' || value === null || typeof value === 'undefined') {
                    this.selected = null;
                    return;
                }
                const match = this.options.find((brand) => String(brand.id) === String(value));
                this.selected = match || null;
            },
        },
        options: {
            deep: true,
            handler() {
                if (this.modelValue === '' || this.modelValue === null) {
                    this.selected = null;
                    return;
                }
                const match = this.options.find((brand) => String(brand.id) === String(this.modelValue));
                this.selected = match || null;
            },
        },
        selected(value) {
            const next = value?.id ?? '';
            if (String(next) !== String(this.modelValue ?? '')) {
                this.$emit('update:modelValue', next);
            }
        },
    },
};
</script>
